import {CardOA} from '@atmi/models/cards/card-oa';
import {oaFilters$} from '@atmi/store/oa-filters.repository';
import {cardFilters$} from '@atmi/store/card-filters.repository';
import {beneF$} from '@atmi/store/bene-f.repository';
import {beneS$} from '@atmi/store/bene-s.repository';
import {beneD$} from '@atmi/store/bene-d.repository';
import {beneMI$} from '@atmi/store/bene-mi.repository';
import {createStore} from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  selectAllEntities,
  selectAllEntitiesApply,
  setEntities,
  updateEntities,
  withEntities
} from '@ngneat/elf-entities';
import {ICompressedOA, ICompressedCard, joinObj, objRenameProps} from '@ui/search-indexing.service';
import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';


export const store = createStore({name: 'oA'}, withEntities<CardOA>());


export const oA$ = store.pipe(selectAllEntities());

export function setAllOA(oA: CardOA[]) {
  store.update(setEntities(oA));
}

export function addOA(oA: CardOA) {
  store.update(addEntities(oA));
}

export function updateOA(id: CardOA['id'], oA: Partial<CardOA>) {
  store.update(updateEntities(id, oA));
}

export function deleteOA(id: CardOA['id']) {
  store.update(deleteEntities(id));
}


const condensedOA$ = store.pipe(
  selectAllEntitiesApply({
    mapEntity: (oa): ICompressedOA => ({
      cardType: 'oa',
      code: (oa.codiceUnico || '').toLowerCase(),
      description: joinObj(oa.DES, true).toLowerCase(),
      id: oa.id || '',
      isParent: !Boolean(oa.benePadre),
      material: (Array.isArray(oa.MTC) ? oa.MTC.join(' ') : oa.MTC || '').toLowerCase(),
      measures: joinObj(objRenameProps(oa.MIS || {}, {
        MISA: 'H',
        MISD: 'D',
        MISG: 'P',
        MISL: 'L',
        MISN: 'Lu',
        MISP: 'Pf',
        MISS: 'S',
        MISU: 'U'
      })).replace('U:cm', ''),
      notes: (oa.note || '').toLowerCase(),
      number: oa.numeroEsemplare || '',
      operaMadre: oa.benePadre || '',
      previewUrl: oa.previewUrl || '',
      price: oa.prodotto?.prezzoDiVendita || 0,
      sgt: (oa.SGT?.SGTT || '').toLowerCase(),
      sgtt: (oa.SGT?.SGTI || '').toLowerCase(),
    })
  })
);

export const filterOaByPriceRange = (oa: ICompressedOA, priceMin: number, priceMax: number): boolean =>
  oa.price >= priceMin && oa.price <= priceMax;

export const filterOaByText = (oa: ICompressedOA, text: string): boolean =>
  (oa.sgt ? oa.sgt.includes(text) : oa.sgtt.includes(text)) || oa.number.includes(text);

export const filterOaByTextToken = (oa: ICompressedOA, tokens: string[]): boolean => {
  return tokens.some(token => oa.sgt.includes(token) || oa.sgtt.includes(token) || oa.number.includes(token));
};

export const filteredOAs$ =
  combineLatest([condensedOA$, oaFilters$])
    .pipe(
      map(([oa, filters]) => {

        const hasPriceFilter = filters.priceMin !== null || filters.priceMax !== null;
        const hasTextFilters = (!filters.tokenized && filters.text !== '') ||
          (filters.tokenized && filters.tokens.length !== 0);

        if (!hasTextFilters && !hasPriceFilter) {
          return filters.emptyOnNoFilter ? [] : oa;
        }

        const isTokenized = filters.tokenized && filters.tokens.length !== 0;

        const priceMin = filters.priceMin ?? 0;
        const priceMax = filters.priceMax ?? Number.MAX_SAFE_INTEGER;
        if (hasPriceFilter && (priceMin > priceMax)) {
          return [];
        }

        return oa.filter(oa =>
          (hasTextFilters && isTokenized ? filterOaByTextToken(oa, filters.tokens) : filterOaByText(oa, filters.text)) ||
          (hasPriceFilter && filterOaByPriceRange(oa, priceMin, priceMax))
        );
      })
    );

// Helper function to create compressed card from different card types
const compressCard = (card: any, cardType: 'f' | 's' | 'd' | 'mi'): ICompressedCard => {
  // Handle material field based on card type
  // BeneF (Fotografie) don't have material, leave blank
  let material = '';
  if (cardType !== 'f') {
    if (Array.isArray(card.MTC)) {
      material = card.MTC.join(' ').toLowerCase();
    } else if (typeof card.MTX === 'string') {
      material = card.MTX.toLowerCase();
    } else if (typeof card.MTC === 'string') {
      material = card.MTC.toLowerCase();
    }
  }

  // Handle SGT field - ensure it's a string
  let sgt = '';
  let sgtt = '';
  if (card.SGT) {
    if (typeof card.SGT.SGTT === 'string') {
      sgt = card.SGT.SGTT.toLowerCase();
    } else if (typeof card.SGT.SGTT === 'object') {
      sgt = joinObj(card.SGT.SGTT, true).toLowerCase();
    }

    if (typeof card.SGT.SGTI === 'string') {
      sgtt = card.SGT.SGTI.toLowerCase();
    } else if (typeof card.SGT.SGTI === 'object') {
      sgtt = joinObj(card.SGT.SGTI, true).toLowerCase();
    }
  }

  return {
    cardType,
    code: (card.codiceUnico || '').toLowerCase(),
    description: joinObj(card.DES, true).toLowerCase(),
    id: card.id || '',
    isParent: !Boolean(card.benePadre),
    material,
    measures: joinObj(card.MIS).replace('U:cm', ''),
    notes: (card.note || '').toLowerCase(),
    number: card.numeroEsemplare || '',
    operaMadre: card.benePadre || '',
    previewUrl: card.previewUrl || '',
    price: card.prodotto?.prezzoDiVendita || 0,
    sgt,
    sgtt,
  };
};

// Compressed cards for all types
export const condensedF$ = beneF$.pipe(
  map(cards => cards.map(card => compressCard(card, 'f')))
);

export const condensedS$ = beneS$.pipe(
  map(cards => cards.map(card => compressCard(card, 's')))
);

export const condensedD$ = beneD$.pipe(
  map(cards => cards.map(card => compressCard(card, 'd')))
);

export const condensedMI$ = beneMI$.pipe(
  map(cards => cards.map(card => compressCard(card, 'mi')))
);

// Unified filtered cards observable (for nav-fab-menu)
export const filteredCards$ =
  combineLatest([condensedOA$, condensedF$, condensedS$, condensedD$, condensedMI$, cardFilters$])
    .pipe(
      map(([oa, f, s, d, mi, filters]) => {
        // Combine all cards
        let allCards: ICompressedCard[] = [];

        // Filter by card type
        if (filters.cardType === 'all') {
          allCards = [...oa, ...f, ...s, ...d, ...mi];
        } else {
          switch (filters.cardType) {
            case 'oa':
              allCards = oa;
              break;
            case 'f':
              allCards = f;
              break;
            case 's':
              allCards = s;
              break;
            case 'd':
              allCards = d;
              break;
            case 'mi':
              allCards = mi;
              break;
          }
        }

        const hasPriceFilter = filters.priceMin !== null || filters.priceMax !== null;
        const hasTextFilters = (!filters.tokenized && filters.text !== '') ||
          (filters.tokenized && filters.tokens.length !== 0);

        if (!hasTextFilters && !hasPriceFilter) {
          return filters.emptyOnNoFilter ? [] : allCards;
        }

        const isTokenized = filters.tokenized && filters.tokens.length !== 0;

        const priceMin = filters.priceMin ?? 0;
        const priceMax = filters.priceMax ?? Number.MAX_SAFE_INTEGER;
        if (hasPriceFilter && (priceMin > priceMax)) {
          return [];
        }

        return allCards.filter(card =>
          (hasTextFilters && isTokenized ? filterOaByTextToken(card, filters.tokens) : filterOaByText(card, filters.text)) ||
          (hasPriceFilter && filterOaByPriceRange(card, priceMin, priceMax))
        );
      })
    );


